import { useCallback, useId, useRef, useState } from "react";
import { renameTaken, splitTaken, type Pic, type Plan } from "../lib/upload";
import { Dialog } from "../ui/Dialog";

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** A folder name, typed or picked from the project's folders; empty means no folder. */
export function FolderField({ value, onChange, folders, onEnter, testId = "folder-field" }: {
  value: string; onChange: (v: string) => void; folders: string[]; onEnter?: () => void; testId?: string;
}) {
  const list = useId();
  return (
    <>
      <input className="input" value={value} list={list} placeholder="No folder" spellCheck={false} maxLength={512} autoFocus
        onChange={(e) => onChange(e.target.value)} data-testid={testId}
        // preventDefault: closing hands focus back to the button that opened the dialog, and Enter would press it again.
        onKeyDown={(e) => { if (e.key === "Enter" && onEnter) { e.preventDefault(); onEnter(); } }} />
      <datalist id={list}>{folders.map((f) => <option key={f} value={f} />)}</datalist>
    </>
  );
}

export interface UploadChoice {
  folder: string;
  /** What to send: the new pictures, and the ones already here under new names if asked. */
  pictures: Pic[];
  /** The zip goes to the ctrlX import with its detections instead (Plan.bundle). */
  detections: boolean;
  /** Pictures already in the project and not sent. */
  skipped: number;
}

/**
 * The one question every upload asks (requested 2026-10-22: "make it simple"): which folder, what to do with pictures
 * the project already has, and, for a detector's captures download, whether its detections come too. Pictures, a
 * folder and zips all arrive here as one Plan (lib/upload.ts).
 */
export function UploadDialog({ plan, taken, folders, onDone }: {
  plan: Plan;
  /** Names the project already has. */
  taken: ReadonlySet<string>;
  /** The project's folders, suggested in the folder field. */
  folders: string[];
  onDone: (choice: UploadChoice | null) => void;
}) {
  const [folder, setFolder] = useState(plan.folder);
  const [detections, setDetections] = useState(false);
  const [addAsNew, setAddAsNew] = useState(false);
  const { fresh, clash } = splitTaken(plan.pictures, taken);
  const prefix = folder.trim() || plan.folder || "copy";
  const renamed = addAsNew ? renameTaken(clash, taken, fresh, prefix) : [];
  const send = detections ? plan.pictures : [...fresh, ...renamed];
  const example = clash.length ? renameTaken(clash.slice(0, 1), taken, fresh, prefix)[0].name : "";

  const done = useRef(onDone);
  done.current = onDone;
  const cancel = useCallback(() => done.current(null), []);
  const go = () => {
    if (send.length) onDone({ folder: folder.trim().replace(/^\/+|\/+$/g, ""), pictures: send, detections, skipped: detections ? 0 : clash.length - renamed.length });
  };

  const notes: string[] = [];
  if (plan.skippedUndisplayable) notes.push(`${plural(plan.skippedUndisplayable, "TIFF/BMP file")} left out: the web app can only show JPEG, PNG and WebP.`);
  if (plan.skippedAnnotated) notes.push(`${plural(plan.skippedAnnotated, "annotated picture")} left out: the device drew its boxes into them.`);
  if (plan.unreadable.length) notes.push(`${plural(plan.unreadable.length, "picture")} cannot be unpacked here (${plan.unreadable[0].reason}).`);

  return (
    <Dialog title={`Upload ${plural(plan.pictures.length, "picture")}`} onClose={cancel} width={480} testId="upload-dialog">
      <div className="upload-form">
        <div className="muted" data-testid="upload-from">From {plan.from}</div>

        <label className="upload-field">
          <span>Into folder</span>
          <FolderField value={folder} onChange={setFolder} folders={folders} onEnter={go} testId="upload-folder-name" />
          <span className="muted">Pick one of the project's folders or type a new name. Leave it empty for no folder.</span>
        </label>

        {plan.bundle && (
          <label className="check upload-check">
            <input type="checkbox" checked={detections} onChange={(e) => setDetections(e.target.checked)} data-testid="upload-detections" />
            <span>Also bring in the device's detections, to review
              <span className="muted"> — this zip is a captures download from a deployed detector</span></span>
          </label>
        )}

        {clash.length > 0 && !detections && (
          <div className="upload-dups" role="radiogroup" aria-label="Pictures already in this project" data-testid="upload-dups">
            <div>{clash.length === plan.pictures.length ? `All ${plural(clash.length, "picture")} are` : `${clash.length} ${clash.length === 1 ? "is" : "are"}`} already
              in this project <span className="muted">({clash.slice(0, 3).map((p) => p.name).join(", ")}{clash.length > 3 ? ", …" : ""})</span></div>
            <label className="check"><input type="radio" name="dups" checked={!addAsNew} onChange={() => setAddAsNew(false)} data-testid="dups-skip" />Skip them</label>
            <label className="check"><input type="radio" name="dups" checked={addAsNew} onChange={() => setAddAsNew(true)} data-testid="dups-new" />
              <span>Add them as new pictures <span className="muted">(renamed, e.g. “{example}”)</span></span></label>
          </div>
        )}
        {detections && <div className="muted">Frames already imported from this device are skipped.</div>}

        {notes.map((n) => <div key={n} className="muted" data-testid="upload-note">{n}</div>)}
      </div>
      <div className="dialog-actions">
        <button className="btn" onClick={cancel}>Cancel</button>
        <button className="btn primary" disabled={!send.length} onClick={go} data-testid="upload-go">
          {send.length ? `Upload ${send.length}` : "Nothing new to upload"}</button>
      </div>
    </Dialog>
  );
}

/** The gallery's Move to folder…: the selected images into one folder, or out of every folder. */
export function MoveDialog({ count, initial, folders, onDone }: {
  count: number; initial: string; folders: string[]; onDone: (folder: string | null) => void;
}) {
  const [folder, setFolder] = useState(initial);
  const done = useRef(onDone);
  done.current = onDone;
  const cancel = useCallback(() => done.current(null), []);
  const go = () => onDone(folder.trim().replace(/^\/+|\/+$/g, ""));
  return (
    <Dialog title={`Move ${plural(count, "image")}`} onClose={cancel} width={420} testId="move-dialog">
      <label className="upload-field">
        <span>Into folder</span>
        <FolderField value={folder} onChange={setFolder} folders={folders} onEnter={go} testId="move-folder-name" />
        <span className="muted">Pick one of the project's folders or type a new name. Leave it empty for no folder.
          Annotations and reviews stay as they are.</span>
      </label>
      <div className="dialog-actions">
        <button className="btn" onClick={cancel}>Cancel</button>
        <button className="btn primary" onClick={go} data-testid="move-go">Move</button>
      </div>
    </Dialog>
  );
}
